// Dependencies
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
// Core & Lab
import {
    Accordion,
    AccordionSummary,
    Typography,
    IconButton,
    AccordionDetails,
    Button,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { KeyboardArrowUp } from '@mui/icons-material';
// Components
import { CircuitName } from './circuitName';
import { CircuitType } from './circuitType';
import CircuitCategories from './circuitCategories';
import { CircuitReference } from './circuitReference';
import { CircuitDestination } from './circuitDestination';
import { CircuitDurationDays } from './circuitDurationDays';
import { CircuitDurationNights } from './circuitDurationNights';
import { setEdit, updateCategories } from './redux/actions';
import { CircuitCategory } from '../circuitCategory/objects/circuitCategory';
import { Circuit } from './objects/circuit';
import { CircuitUserTO } from './circuitUserTO';
import { User } from '../user/objects/user';
import { AppState } from '../../reducers';
import CircuitExternalReference from './circuitExternalReference';
import { updateCircuitInfoErrors } from './redux/reducer';
import { useSnackbar } from 'notistack';
import { setCurrentLocale } from '../locale/redux/actions';
import { checkQuotationCodes } from '../../functions/checkQuotationCode';
import CircuitNbOfIslandsIncluded from './circuitNbOfIslandsIncluded';
// import CircuitDisplayForTripPlan from './circuitDisplayForTripPlan';
// import CircuitDisplayForPackage from './circuitDisplayForPackage';
import CircuitIsProtectedDescription from './circuitIsProtectedDescription';

export type CircuitEditInfoInputs = {
    name: string | undefined,
    localization: Omit<Circuit['localization'][0], 'id'>[],
    destination: number | undefined,
    duration_day: number,
    duration_night: number,
    type: Circuit['type'] | undefined,
    reference: string,
    category: null,
    categories: number[] | undefined,
    userTO: number | null,
}

type Props = {
    circuit: Circuit | null,
    children?: (actions: JSX.Element) => JSX.Element | JSX.Element[]
    onClose?: () => void,
    onSave: (inputs: Partial<CircuitEditInfoInputs>) => void,
    userTO?: { userTO: User | null, setUserTO: React.Dispatch<React.SetStateAction<User | null>> }
}

export function CircuitEditInfo(props: Props): JSX.Element {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const locale = useSelector((state: AppState) => state.locale.current_locale);
    const quotationCode = useSelector((state: AppState) => state.header.quotation_code);
    const has_cms = useSelector((state: AppState) => state.header.has_cms);
    const categories = useSelector((state: AppState) => state.circuit.categories);
    const circuit_name_locales = useSelector((state: AppState) => state.circuit.circuit_name_locales);
    const destination = useSelector((state: AppState) => state.circuit.destination);
    const duration_day = useSelector((state: AppState) => state.circuit.duration_days);
    const duration_night = useSelector((state: AppState) => state.circuit.duration_nights);
    const nb_of_islands_included = useSelector((state: AppState) => state.circuit.nb_of_islands_included);
    const type = useSelector((state: AppState) => state.circuit.type);
    const reference = useSelector((state: AppState) => state.circuit.reference);
    const external_reference = useSelector((state: AppState) => state.circuit.external_reference);
    const is_protected_description = useSelector((state: AppState) => state.circuit.is_protected_description);
    const display_for_package = useSelector((state: AppState) => state.circuit.display_for_package);
    const display_for_trip_plan = useSelector((state: AppState) => state.circuit.display_for_trip_plan);
    const circuit_info_errors = useSelector((state: AppState) => state.circuitSlice.circuit_info_errors);
    const [userTOLocal, setUserTOLocal] = useState<User | null>(null);
    const userTO = props.userTO ? props.userTO.userTO : userTOLocal;
    const setUserTO = props.userTO ? props.userTO.setUserTO : setUserTOLocal;

    const disabled = quotationCode === 'verdie' ?
        !categories?.length :
        false;

    const is_beachcomber = checkQuotationCodes(['beachcomber'], quotationCode);

    const change_only_title_and_description = !is_beachcomber && (props.circuit?.source && ((props.circuit?.source === "TRAVEL_EXCHANGE" || props.circuit?.source === "ADALTE") && !checkQuotationCodes(['volonline'], quotationCode)));

    const change_is_protected_description = is_beachcomber || (props.circuit?.source && props.circuit?.source !== "OFFLINE" && !checkQuotationCodes(['volonline'], quotationCode)) || checkQuotationCodes(['verymed'], quotationCode);

    const change_name = is_beachcomber || change_only_title_and_description || props.circuit?.source === "OFFLINE" || !props.circuit?.source;

    const change_other_fields = is_beachcomber || !props.circuit?.source || props.circuit?.source === 'OFFLINE';

    const onSave = async () => {
        let form_error = circuit_info_errors;
        let is_error = false;
        if (!destination && change_other_fields) {
            form_error = { ...form_error, destination: t('destination-params.destination-required') };
            enqueueSnackbar(t('destination-params.destination-required'), { variant: 'error' });
            is_error = true;
        }
        if (!reference && !change_other_fields) {
            form_error = { ...form_error, reference: t('shared.reference-required') };
            enqueueSnackbar(t('shared.reference-required'), { variant: 'error' });
            is_error = true;
        }
        let no_name = true;
        if (circuit_name_locales[Number(locale || 0)]) {
            no_name = false;
        }
        let too_much_index: null | number = null;
        Object.keys(circuit_name_locales).map((key: string | number) => {
            if ((circuit_name_locales[key]?.length || 0) > 100) {
                too_much_index = Number(key);
            }
        });
        if (no_name) {
            form_error = { ...form_error, name: t('shared.name-required') };
            enqueueSnackbar(t('shared.name-required'), { variant: 'error' });
            is_error = true;
        }
        if (too_much_index) {
            form_error = { ...form_error, name: t('shared.name-should-inf-to-100') };
            dispatch(setCurrentLocale(too_much_index));
            enqueueSnackbar(t('shared.name-should-inf-to-100'), { variant: 'error' });
            is_error = true;
        }
        if (is_error) {
            dispatch(updateCircuitInfoErrors(form_error));
            return;
        }
        let data: any = {
            categories: categories?.map((item) => item.id) ?? undefined,
            type: (type as Circuit['type'] | null) ?? undefined,
        };
        if (change_only_title_and_description) {
            data = {
                ...data,
                name: locale !== null ? circuit_name_locales[locale] : undefined,
                localization: Object.keys(circuit_name_locales).map((key) => {
                    const locale = parseInt(key);
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const { id, ...localizationRest } = props.circuit?.localization.find((item) => item.locale === locale) ?? {
                        id: 0,
                        locale,
                        name: null,
                        sale_pitch: null,
                        board_type: null,
                        condition: null,
                        description: null,
                    };
                    return {
                        ...localizationRest,
                        locale,
                        name: circuit_name_locales[locale] ?? null,
                    };
                }),
            };
        }
        if (change_is_protected_description) {
            data = {
                ...data,
                is_protected_description: !!is_protected_description,
            };
        }
        if (change_other_fields) {
            data = {
                ...data,
                name: locale !== null ? circuit_name_locales[locale] : undefined,
                destination: destination?.id ?? props.circuit?.destination?.id,
                duration_day,
                duration_night,
                external_reference: external_reference || null,
                reference: reference ?? '',
                display_for_package,
                display_for_trip_plan,
                category: null,
                userTO: userTO?.id ?? null,
                is_protected_description: !!is_protected_description,
                nb_of_islands_included: nb_of_islands_included === '' ? null : nb_of_islands_included,
                localization: Object.keys(circuit_name_locales).map((key) => {
                    const locale = parseInt(key);
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const { id, ...localizationRest } = props.circuit?.localization.find((item) => item.locale === locale) ?? {
                        id: 0,
                        locale,
                        name: null,
                        sale_pitch: null,
                        board_type: null,
                        condition: null,
                        description: null,
                    };
                    return {
                        ...localizationRest,
                        locale,
                        name: circuit_name_locales[locale] ?? null
                    };
                }),
            };
        }

        props.onSave(data);
    };

    const onChangeCategories = (categories: CircuitCategory[]) => {
        dispatch(updateCategories(categories));
    };

    const onEditClose = () => {
        dispatch(setEdit(null));
    };

    const actions = (
        <Grid className={'ft-marginTop8'} container spacing={2} justifyContent={'flex-end'}>
            <Grid item>
                <Button onClick={props.onClose ? props.onClose : onEditClose}>
                    {t('shared.cancel')}
                </Button>
            </Grid>
            <Grid item>
                <Button
                    onClick={onSave}
                    disabled={disabled}
                >
                    {t('user.save-change')}
                </Button>
            </Grid>
        </Grid>
    );

    useEffect(() => {
        if (props.circuit) {
            dispatch(updateCategories(props.circuit.categories));
        }
    }, [props.circuit]);

    return (
        <>
            <Grid item xs={12}>
                <Accordion square expanded>
                    <AccordionSummary className={'ft-user-title'}>
                        <Grid container justifyContent={'space-between'} alignItems={'center'}>
                            <Grid item>
                                <Typography>
                                    {
                                        t(
                                            'shared.circuit-informations',
                                            { type: t(`shared.the-${(props.circuit?.type ?? 'circuit').toLowerCase()}`) }
                                        )
                                    }
                                </Typography>
                            </Grid>
                            {
                                !props.onClose &&
                                <Grid item>
                                    <IconButton
                                        onClick={onEditClose}
                                    >
                                        <KeyboardArrowUp className={'ft-user-editProfileDeployed-closeIcon'} />
                                    </IconButton>
                                </Grid>
                            }
                        </Grid>
                    </AccordionSummary>
                    <AccordionDetails className={'ft-user-subContainer'}>
                        <Grid container spacing={2}>
                            {change_name && <Grid item xs={12}>
                                <CircuitName circuit={props.circuit} />
                            </Grid>}
                            {change_other_fields && <>
                                <Grid item xs={12}>
                                    <CircuitReference circuit={props.circuit} />
                                </Grid>
                                <Grid item xs={12}>
                                    <CircuitDestination circuit={props.circuit} />
                                </Grid>
                                <Grid item xs={12}>
                                    <CircuitDurationDays circuit={props.circuit} />
                                </Grid>
                                <Grid item xs={12}>
                                    <CircuitDurationNights circuit={props.circuit} />
                                </Grid>
                                <Grid item xs={12}>
                                    <CircuitUserTO
                                        circuit={props.circuit}
                                        value={userTO}
                                        onChange={setUserTO}
                                    />
                                </Grid>
                            </>}
                            <Grid item xs={12}>
                                <CircuitType circuit={props.circuit} />
                            </Grid>
                            {change_other_fields && <>
                                <Grid item xs={12}>
                                    <CircuitExternalReference circuit={props.circuit} />
                                </Grid>
                                {/*<Grid item xs={12}>*/}
                                {/*    <CircuitDisplayForTripPlan circuit={props.circuit} />*/}
                                {/*</Grid>*/}
                                {/*<Grid item xs={12}>*/}
                                {/*    <CircuitDisplayForPackage circuit={props.circuit} />*/}
                                {/*</Grid>*/}
                                {has_cms && <Grid item xs={12}>
                                    <CircuitNbOfIslandsIncluded
                                        circuit={props.circuit}
                                    />
                                </Grid>}
                            </>}
                            <Grid item xs={12}>
                                <CircuitCategories
                                    categories={categories ?? []}
                                    onChangeCategories={onChangeCategories}
                                />
                            </Grid>
                            {change_is_protected_description && <Grid item xs={12}>
                                <CircuitIsProtectedDescription circuit={props.circuit} />
                            </Grid>}
                        </Grid>
                        {
                            !props.children &&
                            actions
                        }
                    </AccordionDetails>
                </Accordion>
            </Grid>
            {
                props.children &&
                props.children(actions)
            }
        </>
    );
}
